/**
 * Build 7 Part 2 — Subscription state grace banners.
 *
 * Calm, contextual messaging when a member's Shopify subscription is in a
 * non-active state (failed payment, paused, cancelled, expired). Never
 * alarms the user — the language is documentary, not transactional.
 *
 * Renders nothing for active / trialing / null states (silent by design).
 *
 * Reads `subscription_status` from `/auth/me` (Phase 6A webhooks update it).
 */
import React from "react";
import { AlertTriangle, PauseCircle, Clock, RefreshCw, ExternalLink } from "lucide-react";

const MANAGE_URL = process.env.REACT_APP_MANAGE_MEMBERSHIP_URL || "https://thecontextproject.com/account";
// Canonical Shopify membership page — renewal/reactivation goes through here.
const MEMBERSHIP_URL = process.env.REACT_APP_MEMBERSHIP_URL
    || "/membership";

/**
 * Status configuration. Tones are intentionally restrained: amber for
 * action-required, neutral for time-bound, calm cyan for paused.
 */
const STATUS_CONFIG = {
    failed: {
        eyebrow: "Payment issue",
        icon: AlertTriangle,
        headline: "We couldn't process your last payment.",
        body: "Your archive access is still open. Update your card in Shopify to keep things uninterrupted.",
        cta: { label: "Update billing", href: MANAGE_URL, external: true },
        // amber rail — readable, never alarming
        accent: "#E0B341",
        accentBg: "rgba(224, 179, 65, 0.08)",
        accentBorder: "rgba(224, 179, 65, 0.25)",
    },
    paused: {
        eyebrow: "Membership paused",
        icon: PauseCircle,
        headline: "Your investigation is paused.",
        body: "Resume any time from your Shopify account to reopen the deeper archive.",
        cta: { label: "Resume membership", href: MANAGE_URL, external: true },
        accent: "#56CCF2",
        accentBg: "rgba(86, 204, 242, 0.06)",
        accentBorder: "rgba(86, 204, 242, 0.20)",
    },
    cancelled: {
        eyebrow: "Membership ending",
        icon: Clock,
        headline: "Your membership is set to end.",
        body: "You still have access until the current billing period closes. Renew any time to keep the archive open.",
        cta: { label: "Renew membership", href: MEMBERSHIP_URL, external: true },
        accent: "#94A3B8",
        accentBg: "rgba(148, 163, 184, 0.06)",
        accentBorder: "rgba(148, 163, 184, 0.20)",
    },
    expired: {
        eyebrow: "Archive locked",
        icon: RefreshCw,
        headline: "Your membership has lapsed.",
        body: "Reactivate to restore the deeper layer — your saved investigations are waiting.",
        cta: { label: "Reactivate membership", href: MEMBERSHIP_URL, external: true },
        accent: "#94A3B8",
        accentBg: "rgba(148, 163, 184, 0.06)",
        accentBorder: "rgba(148, 163, 184, 0.20)",
    },
};

/**
 * @param {object} props
 * @param {object} props.user — the authenticated user object from /auth/me
 * @param {string} [props.className] — outer spacing control
 */
export default function SubscriptionStatusBanner({ user, className = "" }) {
    if (!user) return null;
    const status = (user.subscription_status || "").toLowerCase();
    const config = STATUS_CONFIG[status];
    if (!config) return null;

    const Icon = config.icon;
    const { cta } = config;

    return (
        <div
            data-testid={`subscription-banner-${status}`}
            className={`relative rounded-xl border px-5 py-4 sm:px-6 sm:py-5 ${className}`}
            style={{
                background: config.accentBg,
                borderColor: config.accentBorder,
            }}
        >
            {/* left accent rail */}
            <span
                aria-hidden="true"
                className="absolute left-0 top-3 bottom-3 w-[2px] rounded-full"
                style={{ background: config.accent }}
            />
            <div className="flex items-start gap-4 pl-3">
                <div
                    className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: config.accentBg, color: config.accent }}
                >
                    <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className="text-[10px] uppercase tracking-[0.25em] font-medium mb-1.5"
                        style={{ color: config.accent }}
                    >
                        {config.eyebrow}
                    </div>
                    <h3 className="text-[15px] sm:text-base font-semibold text-white leading-snug mb-1">
                        {config.headline}
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] text-[#94A3B8] leading-relaxed">
                        {config.body}
                    </p>
                    {cta ? (
                        <a
                            data-testid={`subscription-banner-${status}-cta`}
                            href={cta.href}
                            target={cta.external ? "_blank" : undefined}
                            rel={cta.external ? "noreferrer" : undefined}
                            className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-medium hover:text-white transition-colors"
                            style={{ color: config.accent }}
                        >
                            {cta.label}
                            {cta.external ? <ExternalLink className="w-3.5 h-3.5" /> : null}
                        </a>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
